// ============================================
// AI ASSISTANT COMPONENT
// Clinician-facing AI chat interface
// ============================================

import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageCircle, Send, Bot, User, Loader, 
  FileText, Activity, RefreshCw, ChevronDown, 
  ChevronUp, AlertCircle, Sparkles, X
} from 'lucide-react';
import aiService from './aiService';
import { db, auth } from './firebase';
import {
  collection, addDoc, query, where, orderBy, getDocs, serverTimestamp
} from 'firebase/firestore';

// How much of the conversation is replayed to the model on each turn. The whole
// thread is kept in the record and shown on screen; only the tail is re-sent.
const CONTEXT_TURNS = 20;

const greeting = (patient) =>
  `Ready to assist with ${patient.displayName || 'this patient'}. I can collate visits for continuity, synthesize activity, surface patterns, weigh phase readiness, draft recommendations, and answer specific questions.\n\nEverything I produce is a draft for you. I don't decide and I don't diagnose — and the state nm-analyzer determined is fixed input: I work from it, I don't revise it.`;

// Quick action buttons for common tasks
const QUICK_ACTIONS = [
  {
    id: 'synthesize',
    label: 'Summarize Activity',
    icon: Activity,
    prompt: null, // Uses dedicated function
    description: 'Get an overview of patient activity'
  },
  {
    id: 'phase',
    label: 'Evaluate Phase',
    icon: RefreshCw,
    prompt: null, // Uses dedicated function
    description: 'Assess readiness for phase transition'
  },
  {
    id: 'patterns',
    label: 'Find Patterns',
    icon: Sparkles,
    prompt: 'What patterns do you notice in this patient\'s tool usage and engagement? Are there any concerning trends or positive developments?',
    description: 'Identify engagement patterns'
  },
  {
    id: 'flags',
    label: 'Check Flags',
    icon: AlertCircle,
    prompt: 'Are there any red flags or concerns I should be aware of based on this patient\'s recent activity? Consider both engagement patterns and any assessment data.',
    description: 'Identify potential concerns'
  }
];

function AIAssistant({ patient, activities, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when expanded
  useEffect(() => {
    if (expanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [expanded]);

  // The conversation is part of the clinical record. Dr. Oyelese, 2026-09-26:
  // write-once, never edited or deleted. Each message is its own immutable
  // document, appended as it is spoken and reloaded when the chart reopens.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setHistoryLoading(true);
      let history = [];
      try {
        const q = query(
          collection(db, 'conversations'),
          where('patientId', '==', patient.id),
          orderBy('createdAt', 'asc')
        );
        const snap = await getDocs(q);
        history = snap.docs.map(d => {
          const m = d.data();
          return {
            role: m.role,
            content: m.content,
            isQuickAction: m.isQuickAction || false,
            timestamp: m.createdAt?.toDate?.() || new Date()
          };
        });
      } catch (err) {
        console.error('Could not load the conversation record:', err);
        if (!cancelled) setError('Could not load this chart\u2019s earlier conversation.');
      }
      if (cancelled) return;
      // The greeting is not part of the record — it is shown only when there
      // is no record yet, and never written.
      setMessages(history.length
        ? history
        : [{ role: 'assistant', content: greeting(patient), timestamp: new Date() }]);
      setHistoryLoading(false);
    })();
    return () => { cancelled = true; };
  }, [patient.id, patient.displayName]);

  // Append to the record and to the view together. A failed request is shown
  // but never written: an error is not clinical content.
  const record = async (message) => {
    setMessages(prev => [...prev, message]);
    if (message.role !== 'user' && message.role !== 'assistant') return;
    try {
      await addDoc(collection(db, 'conversations'), {
        patientId: patient.id,
        clinicianId: auth.currentUser?.uid || null,
        role: message.role,
        content: message.content,
        isQuickAction: message.isQuickAction || false,
        createdAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Could not write the message to the record:', err);
      setError('That message was not saved to the record.');
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    record(userMessage);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      // Build conversation history for API
      const apiMessages = messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .slice(-CONTEXT_TURNS)
        .map(m => ({ role: m.role, content: m.content }));
      
      apiMessages.push({ role: 'user', content: input.trim() });

      const response = await aiService.answerQuestion(patient, activities, input.trim());

      record({
        role: 'assistant',
        content: response.content,
        timestamp: new Date()
      });
    } catch (err) {
      setError(err.message || 'Failed to get response');
      setMessages(prev => [...prev, {
        role: 'error',
        content: err.message || 'Failed to get response. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = async (action) => {
    if (loading) return;

    setLoading(true);
    setError(null);

    // Add user message showing what was requested
    record({
      role: 'user',
      content: `[${action.label}]`,
      timestamp: new Date(),
      isQuickAction: true
    });

    try {
      let response;

      switch (action.id) {
        case 'synthesize':
          response = await aiService.synthesizeActivity(patient, activities);
          break;
        case 'phase':
          response = await aiService.evaluatePhaseTransition(patient, activities);
          break;
        default:
          response = await aiService.answerQuestion(patient, activities, action.prompt);
      }

      record({
        role: 'assistant',
        content: response.content,
        timestamp: new Date()
      });
    } catch (err) {
      setError(err.message || 'Failed to get response');
      setMessages(prev => [...prev, {
        role: 'error',
        content: err.message || 'Failed to get response. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (value) => {
    // A reloaded message carries a Firestore Timestamp, a live one a Date.
    const d = value?.toDate ? value.toDate() : value instanceof Date ? value : new Date(value);
    return isNaN(d) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="ai-assistant">
      {/* Header */}
      <div className="ai-header" onClick={() => setExpanded(!expanded)}>
        <div className="ai-header-left">
          <Bot size={20} />
          <span>AI Clinical Coordinator</span>
          <span className="ai-badge">NMA-16</span>
        </div>
        <div className="ai-header-right">
          {expanded ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
          {onClose && (
            <button className="ai-close-btn" onClick={(e) => { e.stopPropagation(); onClose(); }}>
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <>
          {/* Quick Actions */}
          <div className="ai-quick-actions">
            {QUICK_ACTIONS.map(action => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  className="ai-quick-btn"
                  onClick={() => handleQuickAction(action)}
                  disabled={loading}
                  title={action.description}
                >
                  <Icon size={14} />
                  <span>{action.label}</span>
                </button>
              );
            })}
          </div>

          {/* Messages */}
          <div className="ai-messages">
            {messages.map((msg, idx) => (
              <div 
                key={idx} 
                className={`ai-message ${msg.role} ${msg.isQuickAction ? 'quick-action' : ''}`}
              >
                <div className="ai-message-icon">
                  {msg.role === 'user' ? <User size={16} /> : 
                   msg.role === 'error' ? <AlertCircle size={16} /> : 
                   <Bot size={16} />}
                </div>
                <div className="ai-message-content">
                  <div className="ai-message-text">{msg.content}</div>
                  <div className="ai-message-time">{formatTime(msg.timestamp)}</div>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="ai-message assistant loading">
                <div className="ai-message-icon">
                  <Bot size={16} />
                </div>
                <div className="ai-message-content">
                  <div className="ai-typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="ai-input-container">
            <textarea
              ref={inputRef}
              className="ai-input"
              placeholder="Ask about this patient..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              rows={1}
            />
            <button 
              className="ai-send-btn"
              onClick={handleSend}
              disabled={!input.trim() || loading}
            >
              {loading ? <Loader size={18} className="spinning" /> : <Send size={18} />}
            </button>
          </div>

          {/* Footer */}
          <div className="ai-footer">
            <span>AI analysis supports—never replaces—clinical judgment</span>
          </div>
        </>
      )}
    </div>
  );
}

// Simplified panel version for embedding
export function AIAssistantPanel({ patient, activities }) {
  const [isOpen, setIsOpen] = useState(false);

  if (!isOpen) {
    return (
      <button className="ai-fab" onClick={() => setIsOpen(true)}>
        <Bot size={24} />
        <span>AI Assistant</span>
      </button>
    );
  }

  return (
    <div className="ai-panel-overlay">
      <div className="ai-panel">
        <AIAssistant 
          patient={patient} 
          activities={activities} 
          onClose={() => setIsOpen(false)}
        />
      </div>
    </div>
  );
}

export default AIAssistant;
